import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSettingsQuery } from "@/api/queries/admin/SettingsQueries";
import { useSettingsMutation } from "@/api/mutations/admin/SettingsMutations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles } from "lucide-react";
import Loading from "@/components/loading/Loading";

type PersonalityPreset = "professional" | "friendly" | "technical" | "concise" | "educational" | "creative";
type Language = "en" | "fr" | "de" | "zh-tw" | "es" | "it" | "ja" | "ko";

interface PersonalizationSettings {
    aiName?: string;
    personalityPreset?: PersonalityPreset;
    customGreeting?: string;
    primaryLanguage?: Language;
}

const Personalization: React.FC = () => {
    const { t } = useTranslation();
    const { data: settings, isLoading: isLoadingSettings } = useSettingsQuery();
    const { mutate: updateSettings, isLoading: isSaving } = useSettingsMutation("update");

    const [aiName, setAiName] = useState("");
    const [selectedPreset, setSelectedPreset] = useState<PersonalityPreset>("professional");
    const [customGreeting, setCustomGreeting] = useState("");
    const [primaryLanguage, setPrimaryLanguage] = useState<Language>("en");

    // Load settings when data is available
    useEffect(() => {
        if (settings?.personalization) {
            setAiName(settings.personalization.aiName || "");
            setSelectedPreset(settings.personalization.personalityPreset || "professional");
            setCustomGreeting(settings.personalization.customGreeting || "");
            setPrimaryLanguage(settings.personalization.primaryLanguage || "en");
        }
    }, [settings]);

    const presets: Array<{ key: PersonalityPreset; icon: string }> = [
        { key: "professional", icon: "💼" },
        { key: "friendly", icon: "😊" },
        { key: "technical", icon: "⚙️" },
        { key: "concise", icon: "📝" },
        { key: "educational", icon: "📚" },
        { key: "creative", icon: "💡" },
    ];

    const languages: Array<{ code: Language; name: string }> = [
        { code: "en", name: "English" },
        { code: "fr", name: "Français" },
        { code: "de", name: "Deutsch" },
        { code: "zh-tw", name: "繁體中文" },
        { code: "es", name: "Español" },
        { code: "it", name: "Italiano" },
        { code: "ja", name: "日本語" },
        { code: "ko", name: "한국어" },
    ];

    const handleSave = () => {
        const personalization: PersonalizationSettings = {
            aiName: aiName.trim() || undefined,
            personalityPreset: selectedPreset,
            customGreeting: customGreeting.trim() || undefined,
            primaryLanguage,
        };

        updateSettings({
            personalization,
        });
    };

    const handleReset = () => {
        if (window.confirm(t("personalization.confirmReset"))) {
            setAiName("");
            setSelectedPreset("professional");
            setCustomGreeting("");
            setPrimaryLanguage("en");
        }
    };

    if (isLoadingSettings) {
        return (
            <div className="absolute inset-0">
                <Loading />
            </div>
        );
    }

    return (
        <div className="flex w-full flex-col space-y-6">
            {/* Header */}
            <div className="space-y-2">
                <h1 className="text-2xl font-bold">{t("personalization.title")}</h1>
                <p className="text-base text-muted-foreground">{t("personalization.description")}</p>
            </div>

            {/* Basic Settings Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5" />
                        {t("personalization.basicSettings")}
                    </CardTitle>
                    <CardDescription>{t("personalization.basicSettingsDescription")}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* AI Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t("personalization.aiName")}</label>
                        <Input
                            placeholder={t("personalization.aiNamePlaceholder")}
                            value={aiName}
                            onChange={(e) => setAiName(e.target.value)}
                            className="max-w-md"
                        />
                        <p className="text-sm text-muted-foreground">{t("personalization.aiNameDescription")}</p>
                    </div>

                    {/* Custom Greeting */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t("personalization.greeting")}</label>
                        <Textarea
                            placeholder={t("personalization.greetingPlaceholder")}
                            value={customGreeting}
                            onChange={(e) => setCustomGreeting(e.target.value)}
                            className="min-h-[100px] max-w-2xl"
                        />
                        <p className="text-sm text-muted-foreground">{t("personalization.greetingDescription")}</p>
                    </div>

                    {/* Primary Language */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">{t("personalization.primaryLanguage")}</label>
                        <Select value={primaryLanguage} onValueChange={(value: Language) => setPrimaryLanguage(value)}>
                            <SelectTrigger className="max-w-md">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {languages.map((lang) => (
                                    <SelectItem key={lang.code} value={lang.code}>
                                        {lang.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground">{t("personalization.primaryLanguageDescription")}</p>
                    </div>
                </CardContent>
            </Card>

            {/* Personality Preset Section */}
            <Card>
                <CardHeader>
                    <CardTitle>{t("personalization.personalityPreset")}</CardTitle>
                    <CardDescription>{t("personalization.personalityDescription")}</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {presets.map((preset) => (
                            <button
                                key={preset.key}
                                onClick={() => setSelectedPreset(preset.key)}
                                className={`group relative flex flex-col items-start gap-3 rounded-lg border-2 p-4 text-left transition-all hover:border-primary hover:shadow-md ${
                                    selectedPreset === preset.key
                                        ? "border-primary bg-primary/5 shadow-md"
                                        : "border-border bg-card"
                                }`}
                            >
                                <div className="flex w-full items-start justify-between">
                                    <div
                                        className={`flex h-12 w-12 items-center justify-center rounded-lg text-2xl transition-all ${
                                            selectedPreset === preset.key
                                                ? "bg-primary/10"
                                                : "bg-muted group-hover:bg-primary/10"
                                        }`}
                                    >
                                        {preset.icon}
                                    </div>
                                    {selectedPreset === preset.key && (
                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                            <svg
                                                className="h-4 w-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <h3 className="font-semibold">{t(`personalization.presets.${preset.key}.name`)}</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {t(`personalization.presets.${preset.key}.description`)}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex items-center gap-4">
                <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isSaving ? t("personalization.saving") : t("personalization.saveSettings")}
                </Button>
                <Button variant="outline" onClick={handleReset} disabled={isSaving}>
                    {t("personalization.resetToDefault")}
                </Button>
            </div>
        </div>
    );
};

export default Personalization;
